import React from 'react';
import clsx from 'clsx';
import { subscribeReplies } from '../lib/firestore';
import { latestVisibleDiscussionReply } from '../lib/discussionTopicActivity';
import { formatExactDateTime, formatRelativeDateTime } from '../lib/dates';
import { memberLabel, memberPublicProfileUrl, type MemberDirectory } from '../lib/memberDirectory';
import type { DiscussionReply, DiscussionTopic } from '../lib/types';

function MemberAttribution(props: { label: string; profileId: string; displayName: string }) {
  return (
    <span className="flex min-w-0 items-baseline gap-1 whitespace-nowrap">
      <span className="shrink-0 italic">{props.label}</span>
      <a
        href={memberPublicProfileUrl(props.profileId)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        className="min-w-0 truncate text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
        title={props.displayName}
      >
        {props.displayName}
      </a>
    </span>
  );
}

export function DiscussionTopicFooter(props: { topic: DiscussionTopic; directory: MemberDirectory }) {
  const [latestReply, setLatestReply] = React.useState<DiscussionReply | null>(null);

  React.useEffect(() => {
    setLatestReply(null);
    return subscribeReplies(props.topic.id, (replies) => setLatestReply(latestVisibleDiscussionReply(replies)), () => setLatestReply(null));
  }, [props.topic.id]);

  const author = memberLabel(props.topic.authorProfileId, props.topic.authorDisplayName, props.directory);
  const replier = latestReply ? memberLabel(latestReply.authorProfileId, latestReply.authorDisplayName, props.directory) : '';
  const activityAt = latestReply?.createdAt ?? props.topic.createdAt;

  return (
    <div className={clsx(
      'mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 text-xs text-zinc-600',
      latestReply && 'sm:grid-cols-[fit-content(45%)_minmax(0,1fr)_auto]'
    )}>
      <div className={clsx('min-w-0', latestReply && 'col-span-2 sm:col-span-1')}>
        <MemberAttribution label="Created by:" profileId={props.topic.authorProfileId} displayName={author} />
      </div>
      {latestReply ? (
        <MemberAttribution label="Last replied:" profileId={latestReply.authorProfileId} displayName={replier} />
      ) : null}
      <span className="whitespace-nowrap text-right" title={formatExactDateTime(activityAt)}>
        {formatRelativeDateTime(activityAt)}
      </span>
    </div>
  );
}
